<template>
  <v-container fluid class="pb-12">
    <v-row class="align-center px-3 pt-3 mb-2">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-3">
          <v-icon color="white" small>fas fa-truck-ramp-box</v-icon>
        </v-avatar>
        <div v-if="batch" class="text-left">
          <div class="text-h5 font-weight-black primary--text">Lote de guías #{{ batch.id }}<span v-if="batch.name" class="font-weight-medium"> · {{ batch.name }}</span></div>
          <div class="caption grey--text">
            <span v-if="batch.workorder">OT {{ batch.workorder }} · </span>{{ batch.customer_name }} ({{ batch.customer_rut }})
            <span v-if="batch.file_name"> · último Excel: {{ batch.file_name }}</span>
          </div>
        </div>
      </div>
      <v-spacer />
      <v-btn text small @click="$router.push({ name: 'BulkDispatch' })"><v-icon left small>fas fa-arrow-left</v-icon>Lotes</v-btn>
    </v-row>

    <div class="d-flex justify-center mt-12" v-if="!batch"><v-progress-circular indeterminate color="primary" /></div>

    <template v-else>
      <!-- Emisión / progreso -->
      <v-card outlined class="pa-4 mb-4" :class="isRunning ? 'blue lighten-5' : ''">
        <div class="d-flex align-center flex-wrap">
          <v-chip small :color="batchStatus.color" dark>{{ batchStatus.text }}</v-chip>
          <span class="ml-3">
            <b>{{ summary.emitted }}</b> de {{ summary.total }} emitidas
            <span v-if="summary.queued"> · {{ summary.queued }} en cola</span>
            <span v-if="counts.error"> · <span class="error--text">{{ counts.error }} con error</span></span>
            <span v-if="counts.revisar"> · <span class="warning--text">{{ counts.revisar }} por revisar</span></span>
            <span v-if="isRunning && etaText"> · {{ etaText }}</span>
          </span>
          <v-spacer />
          <v-btn small text color="primary" :disabled="isRunning" :loading="refreshing" @click="manualRefresh" title="Actualizar"><v-icon small>fas fa-arrows-rotate</v-icon></v-btn>
          <v-btn small text color="primary" :disabled="isRunning" @click="importDialog = true"><v-icon left small>fas fa-file-excel</v-icon>Cargar otro Excel</v-btn>
          <v-btn small text color="primary" :loading="exporting" @click="exportResult"><v-icon left small>fas fa-download</v-icon>Descargar resultado</v-btn>
        </div>
        <v-progress-linear class="mt-3" rounded height="8" :value="percent" :color="isRunning ? 'primary' : 'success'" />
        <div v-if="batch.message && !isRunning" class="caption mt-2" :class="batch.status === 'paused' ? 'warning--text' : 'grey--text'">{{ batch.message }}</div>
        <div v-if="summary.interrupted" class="caption warning--text mt-2">
          El proceso se interrumpió (probablemente se reinició el servidor). Pulse «Reanudar» para continuar; las guías que estaban emitiéndose quedan «por revisar».
        </div>
        <div v-if="isRunning" class="caption grey--text mt-1">Puede salir de esta página: la emisión continúa en el servidor. Vuelva desde «Guías masivas» para ver el avance o sumar más guías a la cola.</div>

        <div class="mt-3 d-flex flex-wrap" style="gap: 8px">
          <v-btn small color="success" :disabled="!selectedEmittable.length || !!starting" :loading="starting === 'selected'" @click="askEmit(true)">
            <v-icon left small>fas fa-paper-plane</v-icon>{{ isRunning ? 'Agregar seleccionadas a la cola' : 'Emitir seleccionadas' }} ({{ selectedEmittable.length }})
          </v-btn>
          <v-btn small color="success" outlined :disabled="!emittableItems.length || !!starting" :loading="starting === 'all'" @click="askEmit(false)">
            {{ isRunning ? 'Agregar todas a la cola' : 'Emitir todas' }} ({{ emittableItems.length }})
          </v-btn>
          <v-btn small color="warning" :disabled="!isRunning" :loading="busy.pause" @click="pause"><v-icon left small>fas fa-pause</v-icon>Pausar</v-btn>
          <v-btn small color="primary" :disabled="!(batch.status === 'paused' || summary.interrupted) || !summary.queued" :loading="busy.resume" @click="resume"><v-icon left small>fas fa-play</v-icon>Reanudar</v-btn>
          <v-btn small text color="error" :disabled="batch.status === 'idle' && !summary.queued" :loading="busy.stop" @click="askStop"><v-icon left small>fas fa-stop</v-icon>Detener</v-btn>
        </div>
      </v-card>

      <!-- Parámetros (cabecera de las guías) -->
      <v-expansion-panels flat class="mb-4 border">
        <v-expansion-panel>
          <v-expansion-panel-header class="font-weight-bold">
            Datos de las guías
            <span class="caption grey--text ml-3">traslado {{ params.tipo_traslado }} · precio unitario {{ params.precio_unitario }}<span v-if="params.oc_folio"> · OC {{ params.oc_folio }}</span></span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row dense>
              <v-col cols="12"><v-text-field dense outlined hide-details label="Nombre del lote" v-model="batchName" maxlength="150" placeholder="Ej: Jumbo Semana Alemana 2026" /></v-col>
              <v-col cols="12" md="4"><v-select dense outlined hide-details :items="traslados" item-text="text" item-value="value" label="Indicador de traslado" v-model="params.tipo_traslado" :disabled="isRunning" /></v-col>
              <v-col cols="12" md="4"><v-select dense outlined hide-details :items="despachos" item-text="text" item-value="value" label="Tipo de despacho" v-model="params.tipo_despacho" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="2"><v-text-field dense outlined hide-details type="number" min="0" label="Precio unitario" v-model.number="params.precio_unitario" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="2"><v-text-field dense outlined hide-details type="date" label="Fecha guías" v-model="params.fecha" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details label="N° Orden de compra" v-model="params.oc_folio" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details type="date" label="Fecha OC" v-model="params.oc_fecha" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details label="Patente" v-model="params.transporte.patente" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details label="RUT transportista" v-model="params.transporte.rut_trans" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details label="Chofer" v-model="params.transporte.nombre_chofer" :disabled="isRunning" /></v-col>
              <v-col cols="6" md="3"><v-text-field dense outlined hide-details label="RUT chofer" v-model="params.transporte.rut_chofer" :disabled="isRunning" /></v-col>
            </v-row>
            <v-btn small color="primary" class="mt-3" :disabled="isRunning" :loading="savingParams" @click="saveParams">Guardar datos</v-btn>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Pestañas -->
      <v-tabs v-model="tab" class="mb-2">
        <v-tab>Pre-guías <v-chip x-small class="ml-2">{{ pendingItems.length }}</v-chip></v-tab>
        <v-tab>Emitidas <v-chip x-small class="ml-2" color="success" dark>{{ emittedItems.length }}</v-chip></v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" style="background: transparent">
        <v-tab-item>
          <v-card outlined>
            <v-data-table
              v-model="selected" :headers="preHeaders" :items="preItems" item-key="id" show-select
              show-expand :expanded.sync="expanded"
              :items-per-page="25" :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
              dense no-data-text="No hay pre-guías pendientes" :search="search"
            >
              <template v-slot:top>
                <div class="pa-3 d-flex align-center">
                  <v-text-field v-model="search" dense outlined hide-details label="Buscar local" prepend-inner-icon="fas fa-search" style="max-width: 320px" />
                  <v-checkbox v-model="onlyIssues" dense hide-details label="Solo con error / por revisar" class="ml-4 mt-0" />
                </div>
              </template>
              <template v-slot:item.data-table-select="{ item, isSelected, select }">
                <v-simple-checkbox :value="isSelected" :disabled="!isEmittable(item)" @input="select($event)" />
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0"><guide-preview :item="item" :params="batch.params" :customer="customerInfo" :default-comuna="batch.default_comuna" /></td>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip x-small :color="statusMeta(item.status).color" dark>{{ statusMeta(item.status).text }}</v-chip>
                <div v-if="item.error" class="caption error--text" style="max-width: 360px; white-space: normal">{{ item.error }}</div>
              </template>
              <template v-slot:item.total_units="{ item }">{{ item.total_units }} <span class="grey--text caption">({{ item.lines.length }} líneas)</span></template>
              <template v-slot:item.comuna="{ item }">
                <span v-if="item.comuna">{{ item.comuna }}</span>
                <span v-else class="grey--text caption" title="Sin comuna en el Excel: se usa la comuna del cliente">{{ batch.default_comuna || '—' }} *</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn v-if="item.editable" icon x-small @click="openEdit(item)"><v-icon x-small>fas fa-pen</v-icon></v-btn>
                <v-btn v-if="item.editable" icon x-small :loading="busy.row === item.id" @click="askRemove(item)"><v-icon x-small color="error">fas fa-trash</v-icon></v-btn>
                <v-btn v-if="item.status === 'revisar' && !item.folio" x-small text color="warning" :loading="busy.row === item.id" @click="askRequeue(item)">Volver a borrador</v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card outlined>
            <v-data-table :headers="emittedHeaders" :items="emittedItems" item-key="id" dense :items-per-page="25" no-data-text="Aún no hay guías emitidas" show-expand>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0"><guide-preview :item="item" :params="batch.params" :customer="customerInfo" :default-comuna="batch.default_comuna" /></td>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small text color="primary" :loading="busy.pdf === item.id" :disabled="!!busy.pdf" @click="openPdf(item)"><v-icon left x-small>fas fa-file-pdf</v-icon>PDF</v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <!-- Edición de una pre-guía -->
    <v-dialog v-model="editDialog" max-width="760" persistent>
      <v-card v-if="editItem">
        <v-card-title>Pre-guía {{ editItem.local_code }}</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="5"><v-text-field dense outlined hide-details label="Nombre local" v-model="editItem.local_name" /></v-col>
            <v-col cols="12" md="5"><v-text-field dense outlined label="Dirección de despacho" v-model="editItem.address" counter="70" /></v-col>
            <v-col cols="12" md="2"><v-text-field dense outlined label="Comuna" v-model="editItem.comuna" counter="20" /></v-col>
          </v-row>
          <v-simple-table dense>
            <thead><tr><th>Producto</th><th style="width: 110px">Cantidad</th><th style="width: 40px"></th></tr></thead>
            <tbody>
              <tr v-for="(line, i) in editItem.lines" :key="i">
                <td class="caption">{{ line.name }}</td>
                <td><v-text-field dense hide-details type="number" min="1" v-model.number="line.qty" /></td>
                <td><v-btn icon x-small @click="editItem.lines.splice(i, 1)"><v-icon x-small color="error">fas fa-times</v-icon></v-btn></td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="savingItem" @click="editDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingItem" @click="saveEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <bulk-dispatch-import-dialog v-model="importDialog" :batch="batch" @loaded="onReloaded" />
    <confirm v-model="confirmShow" :message="confirmMessage" @ok="runConfirmed" @cancel="confirmShow = false" />
  </v-container>
</template>

<script>
import notifications from '@/utils/notifications'
import BulkDispatchImportDialog from '@/components/facturacioncl/BulkDispatchImportDialog.vue'
import GuidePreview from '@/components/facturacioncl/BulkDispatchGuidePreview.vue'
import {
  getDispatchBatch, updateDispatchParams, startDispatchBatch, resumeDispatchBatch, pauseDispatchBatch,
  stopDispatchBatch, exportDispatchBatch, updateDispatchItem, deleteDispatchItem, requeueDispatchItem,
  downloadDocumentPdf,
} from '@/api/facturacioncl'

const STATUS_META = {
  borrador: { text: 'Borrador', color: 'grey' },
  en_cola: { text: 'En cola', color: 'info' },
  emitiendo: { text: 'Emitiendo…', color: 'primary' },
  emitida: { text: 'Emitida', color: 'success' },
  error: { text: 'Error', color: 'error' },
  revisar: { text: 'Por revisar', color: 'warning' },
}

function emptyParams() {
  return { tipo_traslado: 1, tipo_despacho: 1, precio_unitario: 1, fecha: null, oc_folio: '', oc_fecha: null, transporte: {} }
}

export default {
  name: 'BulkDispatchBatch',
  components: { BulkDispatchImportDialog, GuidePreview },
  data() {
    return {
      batch: null,
      params: emptyParams(),
      batchName: '',
      savingParams: false,
      starting: null, // 'selected' | 'all'
      refreshing: false,
      busy: { pause: false, resume: false, stop: false, row: null, pdf: null },
      exporting: false,
      selected: [],
      expanded: [],
      tab: 0,
      search: '',
      onlyIssues: false,
      timer: null,
      editDialog: false,
      editItem: null,
      savingItem: false,
      importDialog: false,
      confirmShow: false,
      confirmMessage: '',
      confirmAction: null,
      traslados: [
        { value: 1, text: '1 - Operación constituye venta' },
        { value: 2, text: '2 - Ventas por efectuar' },
        { value: 3, text: '3 - Consignaciones' },
        { value: 4, text: '4 - Entrega gratuita' },
        { value: 5, text: '5 - Traslados internos' },
        { value: 6, text: '6 - Otros traslados no venta' },
        { value: 7, text: '7 - Guía de devolución' },
      ],
      despachos: [
        { value: 1, text: '1 - Despacho por cuenta del receptor' },
        { value: 2, text: '2 - Por cuenta del emisor a cliente' },
        { value: 3, text: '3 - Por cuenta del emisor a otras instalaciones' },
      ],
      preHeaders: [
        { text: 'N° Local', value: 'local_code', width: 90 },
        { text: 'Nombre', value: 'local_name' },
        { text: 'Dirección', value: 'address' },
        { text: 'Comuna', value: 'comuna', width: 110 },
        { text: 'Unidades', value: 'total_units', align: 'end', width: 140 },
        { text: 'Estado', value: 'status' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 150 },
      ],
      emittedHeaders: [
        { text: 'Folio', value: 'folio', width: 100 },
        { text: 'N° Local', value: 'local_code', width: 90 },
        { text: 'Nombre', value: 'local_name' },
        { text: 'Dirección', value: 'address' },
        { text: 'Unidades', value: 'total_units', align: 'end', width: 100 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 100 },
      ],
    }
  },
  computed: {
    batchId() { return this.$route.params.batchId },
    customerInfo() { return this.batch ? { name: this.batch.customer_name, rut: this.batch.customer_rut } : null },
    items() { return (this.batch && this.batch.items) || [] },
    summary() { return (this.batch && this.batch.summary) || { total: 0, emitted: 0, queued: 0, counts: {}, interrupted: false } },
    counts() { return this.summary.counts || {} },
    isRunning() { return !!this.batch && this.batch.status === 'running' && !this.summary.interrupted },
    pendingItems() { return this.items.filter(i => i.status !== 'emitida') },
    preItems() {
      return this.onlyIssues ? this.pendingItems.filter(i => i.status === 'error' || i.status === 'revisar') : this.pendingItems
    },
    emittedItems() { return this.items.filter(i => i.status === 'emitida') },
    emittableItems() { return this.items.filter(this.isEmittable) },
    selectedEmittable() { return this.selected.filter(this.isEmittable) },
    percent() { return this.summary.total ? Math.round((this.summary.emitted / this.summary.total) * 100) : 0 },
    etaText() {
      const s = this.summary.eta_seconds
      if (!s) return ''
      return s < 90 ? 'quedan ~' + s + ' s' : 'quedan ~' + Math.ceil(s / 60) + ' min'
    },
    batchStatus() {
      if (!this.batch) return { text: '', color: 'grey' }
      if (this.summary.interrupted) return { text: 'Interrumpido', color: 'warning' }
      if (this.batch.status === 'running') return { text: 'Emitiendo', color: 'primary' }
      if (this.batch.status === 'paused') return { text: 'Pausado', color: 'warning' }
      if (this.summary.total && this.summary.emitted === this.summary.total) return { text: 'Terminado', color: 'success' }
      return { text: 'Detenido', color: 'grey' }
    },
  },
  watch: {
    batchId: { immediate: true, handler() { this.load() } },
  },
  beforeDestroy() {
    this.destroyed = true
    this.stopPolling()
  },
  methods: {
    statusMeta(s) { return STATUS_META[s] || { text: s, color: 'grey' } },
    isEmittable(i) { return i.status === 'borrador' || i.status === 'error' },
    fail(e) {
      let msg = 'Ocurrió un error'
      if (typeof e === 'string') msg = e
      else if (e && e.response && e.response.data) {
        const d = e.response.data
        const first = d.error || Object.values(d)[0]
        msg = Array.isArray(first) ? first[0] : (typeof first === 'string' ? first : msg)
      }
      notifications.showError(msg)
    },
    ask(message, action) {
      this.confirmMessage = message
      this.confirmAction = action
      this.confirmShow = true
    },
    async runConfirmed() {
      this.confirmShow = false
      const action = this.confirmAction
      this.confirmAction = null
      if (action) await action()
    },
    setBatch(b, syncParams = false) {
      const wasRunning = this.batch && this.batch.status === 'running'
      this.batch = b
      // el polling no pisa los datos que el usuario está editando
      if (syncParams) {
        this.batchName = b.name || ''
        this.params = Object.assign(emptyParams(), b.params || {})
        this.params.transporte = Object.assign({}, (b.params && b.params.transporte) || {})
      }
      const ids = new Set(this.emittableItems.map(i => i.id))
      this.selected = this.selected.filter(i => ids.has(i.id))
      if (wasRunning && b.status !== 'running') this.notifyFinished(b)
      this.syncPolling()
    },
    async load() {
      this.stopPolling()
      this.batch = null
      try {
        const b = await getDispatchBatch(this.batchId)
        if (!this.destroyed) this.setBatch(b, true)
      } catch (e) {
        this.fail(e)
      }
    },
    async refresh() {
      if (!this.batch || this.destroyed) return
      try {
        const b = await getDispatchBatch(this.batch.id)
        if (!this.destroyed) this.setBatch(b)
      } catch (e) { /* fallo de red puntual: se reintenta en el siguiente ciclo */ }
    },
    async manualRefresh() {
      this.refreshing = true
      try { await this.refresh() } finally { this.refreshing = false }
    },
    onReloaded(b) {
      this.setBatch(b, true)
      this.tab = 0
    },
    async saveParams(silent) {
      this.savingParams = true
      try {
        const b = await updateDispatchParams(this.batch.id, { ...this.params, name: this.batchName })
        this.batch = { ...this.batch, params: b.params, name: b.name }
        if (silent !== true) notifications.showSuccess('Datos de las guías guardados')
        return true
      } catch (e) {
        this.fail(e)
        return false
      } finally {
        this.savingParams = false
      }
    },
    askEmit(onlySelected) {
      const n = onlySelected ? this.selectedEmittable.length : this.emittableItems.length
      const msg = this.isRunning
        ? 'Se agregarán ' + n + ' pre-guías a la cola de emisión en curso.'
        : 'Se emitirán ' + n + ' guías de despacho en Facturación.cl, a ritmo controlado. ¿Desea continuar?'
      this.ask(msg, () => this.emit(onlySelected))
    },
    async emit(onlySelected) {
      const ids = onlySelected ? this.selectedEmittable.map(i => i.id) : null
      this.starting = onlySelected ? 'selected' : 'all'
      try {
        if (!this.isRunning && !(await this.saveParams(true))) return
        const res = await startDispatchBatch(this.batch.id, { item_ids: ids, include_errors: true })
        this.selected = []
        this.setBatch(res)
        if (res.message_detail) notifications.showSuccess(res.message_detail)
      } catch (e) {
        this.fail(e)
      } finally {
        this.starting = null
      }
    },
    async withBusy(key, value, fn) {
      this.busy[key] = value
      try { await fn() } catch (e) { this.fail(e) } finally { this.busy[key] = key === 'row' || key === 'pdf' ? null : false }
    },
    resume() {
      return this.withBusy('resume', true, async () => { this.setBatch(await resumeDispatchBatch(this.batch.id)) })
    },
    pause() {
      return this.withBusy('pause', true, async () => {
        this.setBatch(await pauseDispatchBatch(this.batch.id))
        notifications.showSuccess('Pausado', 'Se detendrá al terminar la guía en curso')
      })
    },
    askStop() {
      this.ask('¿Detener el proceso? Las pre-guías en cola vuelven a borrador.', () =>
        this.withBusy('stop', true, async () => { this.setBatch(await stopDispatchBatch(this.batch.id)) })
      )
    },
    async exportResult() {
      this.exporting = true
      try { await exportDispatchBatch(this.batch.id) } catch (e) { this.fail(e) } finally { this.exporting = false }
    },
    openEdit(item) {
      this.editItem = JSON.parse(JSON.stringify(item))
      this.editDialog = true
    },
    async saveEdit() {
      this.savingItem = true
      try {
        await updateDispatchItem(this.editItem.id, {
          local_name: this.editItem.local_name,
          address: this.editItem.address,
          comuna: this.editItem.comuna,
          lines: this.editItem.lines.map(l => ({ name: l.name, description: l.description, qty: l.qty })),
        })
        this.editDialog = false
        await this.refresh()
      } catch (e) {
        this.fail(e)
      } finally {
        this.savingItem = false
      }
    },
    askRemove(item) {
      this.ask('¿Descartar la pre-guía del local ' + item.local_code + '?', () =>
        this.withBusy('row', item.id, async () => { await deleteDispatchItem(item.id); await this.refresh() })
      )
    },
    askRequeue(item) {
      this.ask(
        'Confirme que verificó en Facturación.cl que la guía del local ' + item.local_code + ' NO fue emitida. Volverá a borrador para emitirla de nuevo.',
        () => this.withBusy('row', item.id, async () => { await requeueDispatchItem(item.id); await this.refresh() })
      )
    },
    openPdf(item) {
      return this.withBusy('pdf', item.id, () => downloadDocumentPdf(item.document_id))
    },
    notifyFinished(b) {
      const s = b.summary
      if (b.status === 'paused') notifications.showError('Emisión pausada', b.message)
      else if (s.counts.error || s.counts.revisar) notifications.showError('Emisión terminada con observaciones', (s.counts.error || 0) + ' con error, ' + (s.counts.revisar || 0) + ' por revisar')
      else notifications.showSuccess('Emisión terminada', s.emitted + ' guías emitidas')
    },
    syncPolling() {
      // Solo mientras hay un proceso vivo; un lote interrumpido no avanza solo, no tiene sentido consultarlo
      const active = !this.destroyed && this.batch && !this.summary.interrupted &&
        (this.batch.status === 'running' || (this.counts.emitiendo || 0) > 0)
      if (active && !this.timer) this.timer = setInterval(this.refresh, 2000)
      else if (!active) this.stopPolling()
    },
    stopPolling() {
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    },
  },
}
</script>
